//* Import libraries
import React, { useState, useCallback, useEffect } from "react";

//* Import react-router hooks
import { useNavigate } from "react-router-dom";

//* Import components
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Import custom hooks
import { useCourses } from "../../../hooks/courses/useCourses";

//* Import types
type Course = {
  id: string;
  name: string;
  description?: string;
};

var debounce = require("lodash.debounce");

export function CourseView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalCourses, setTotalCourses] = useState<number>(0);

  const {
    courses,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteCourse,
    refetch,
    changePage,
    isDeleting,
  } = useCourses(token);

  useEffect(() => {
    setTotalCourses(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (courseToDelete) {
      try {
        const currentPageItems = courses.length;
        const currentPage = pagination.currentPage;

        await deleteCourse(courseToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setCourseToDelete(null);
      } catch (error) {
        console.error("Error deleting admin:", error);
      }
    }
  }, [
    courseToDelete,
    deleteCourse,
    courses.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setCourseToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  if (isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        search_text="Pesquise pelo nome da curso"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Cursos (Total: ${totalCourses})`}
        not_found_message="Nenhum curso encontrado"
        button_link="/courses/create"
        button_text="Adicionar curso"
        deleting_message="Ao deletar este cursos, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/courses/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        onManagerActionClick={(value) => {
          navigate(`/courses/view/${value}`, {
            state: { filter: value, key: "course_id" },
          });
        }}
        items={courses.map((course: Course) => ({
          ...course,
          key: course.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "description", type: "text", header: "Descrição" },
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
